import React, { memo, useMemo } from 'react'
import PropTypes from 'prop-types'
import { FastField } from 'formik'
import ScorecardSelector from '@v4/utils/selectors/ScorecardSelector'
import QUESTION_TYPE from '@v4/constants/questions'
import useWordings from '@v4/hooks/useWordings'
import Autocomplete from './components/Autocomplete'

const FormSelectField = props => {
	const { options, type, layout = 'transparent', label = '', required = false } = props
	const { getWording: getCountryWording, ready: countriesReady } = useWordings('countries')
	const { getWording: getIndustryWording, ready: industriesReady } = useWordings('industries')
	const localeTextDirection = ScorecardSelector.useSettings()?.locale_text_direction || 'ltr'
	const locale = ScorecardSelector.useLocale()

	const translatedOptions = useMemo(() => {
		if (!_.includes([QUESTION_TYPE.COUNTRY, QUESTION_TYPE.INDUSTRY], type)) return options

		const getTranslation = type === QUESTION_TYPE.COUNTRY ? getCountryWording : getIndustryWording

		const result = _.map(options, option => ({
			...option,
			option: option.option && !option.is_custom ? getTranslation(option.option) : option.option,
		}))

		return result.sort((a, b) => a.option.localeCompare(b.option, locale, { sensitivity: 'base' }))
	}, [getCountryWording, getIndustryWording, locale, options, type])

	return (
		<FastField key={`${layout}_${label}_${required}_${!!(industriesReady && countriesReady)}`} {...props}>
			{({ field, meta: { touched, error }, form: { setFieldValue } }) => {
				const errorText = touched ? error : ''

				return (
					<Autocomplete
						fullWidth
						{...props}
						layout={layout}
						label={label}
						required={required}
						options={translatedOptions}
						{...field}
						onChange={(event, { id }) => {
							setFieldValue(field.name, id)
						}}
						localeTextDirection={localeTextDirection}
						error={!!errorText && touched}
						helperText={errorText}
					/>
				)
			}}
		</FastField>
	)
}

FormSelectField.propTypes = {
	name: PropTypes.string.isRequired,
	// eslint-disable-next-line react/no-unused-prop-types
	usePaperColors: PropTypes.bool,
	label: PropTypes.string,
	required: PropTypes.bool,
	layout: PropTypes.string,
	settings: PropTypes.object,
	options: PropTypes.array,
	type: PropTypes.string,
}

export default memo(FormSelectField)
